/**
 * Envio de pastas e subpastas no gerenciador (out/2026).
 *
 * O backend já aceitava caminho com subpastas no `path` do upload — a pasta
 * nasce do `folder_path` do asset, sem precisar existir antes. O que faltava
 * era o navegador entregar a árvore:
 *
 * - **Seletor de pasta** (`<input webkitdirectory>`): cada `File` traz o
 *   `webkitRelativePath` («Pasta/sub/arte.pdf»). Pasta vazia não aparece.
 * - **Arrastar**: `dataTransfer.files` traz a pasta como um arquivo vazio, sem
 *   o conteúdo. A árvore vem de `webkitGetAsEntry()`, que precisa ser lido
 *   **antes do primeiro `await`** do `drop` (depois o navegador esvazia o
 *   `DataTransfer`), e `readEntries` devolve em lotes (o Chrome, de 100): sem
 *   repetir até vir vazio, uma pasta com 150 arquivos sobe 100 em silêncio.
 *
 * Pasta real sempre traz lixo de sistema (`.DS_Store`, `._*`, `Thumbs.db`,
 * `__MACOSX`): ele é pulado e só contado. O que não é aceito (um `.txt`, um
 * `.zip`) também é pulado, mas listado — antes, um arquivo desses recusava o
 * lote inteiro.
 *
 * Mesmo nome no destino **mescla**: o caminho é a identidade do asset, então o
 * mesmo conteúdo não duplica e conteúdo diferente vira versão nova, como no
 * SFTP.
 */

export const SUPPORTED_UPLOAD_EXTENSIONS = [".pdf", ".ai", ".jpg", ".jpeg", ".png", ".tif", ".tiff"];
export const SUPPORTED_UPLOAD_MIME_TYPES = new Set([
  "application/pdf",
  // O `.ai` (set/2026): cada sistema declara um MIME diferente para ele —
  // PostScript no macOS e no Windows com Illustrator, «illustrator» em
  // alguns Linux, PDF em outros. O que decide é a assinatura, no servidor.
  "application/postscript",
  "application/illustrator",
  "image/jpeg",
  "image/png",
  "image/tiff",
]);

/**
 * Os tetos das colunas `assets.path`/`folders.path` (1024) e
 * `assets.filename`/`files.filename` (512) — os mesmos de
 * `MAX_PATH_CHARS`/`MAX_SEGMENT_CHARS` em `backend/app/services/asset_path.py`.
 * O servidor recusa de qualquer jeito; conferir aqui evita subir 1 GiB para
 * ouvir um 400.
 */
export const MAX_PATH_CHARS = 1024;
export const MAX_SEGMENT_CHARS = 512;

/** A partir daqui o envio pede confirmação: arrastar a pasta errada custa caro. */
export const CONFIRM_ABOVE_FILES = 200;
export const CONFIRM_ABOVE_BYTES = 2 * 1024 ** 3;

const SYSTEM_FILE_NAMES = new Set(["thumbs.db", "ehthumbs.db", "desktop.ini", "icon\r"]);
const SYSTEM_DIR_NAMES = new Set(["__macosx", "$recycle.bin", "system volume information"]);

export type SkipReason = "unsupported" | "too_long" | "unreadable" | "folder_error";

export interface SkippedItem {
  /** Caminho relativo ao que foi solto/escolhido, como o usuário reconhece. */
  relativePath: string;
  reason: SkipReason;
  detail?: string;
}

export interface UploadCandidate {
  file: File;
  /** Relativo, com `/`, sem barra inicial: «Pasta/sub/arte.pdf» ou «arte.pdf». */
  relativePath: string;
}

export interface CollectedUpload {
  candidates: UploadCandidate[];
  /** Pastas vistas na árvore (relativas). Só o arrastar enxerga pasta vazia. */
  directories: string[];
  /** Lixo de sistema já descartado na coleta (não entra nem na lista de pulados). */
  systemCount: number;
  /** O que não deu para ler do disco (permissão, arquivo que sumiu). */
  unreadable: SkippedItem[];
}

export interface PlannedUpload {
  file: File;
  /** Caminho lógico completo: pasta de destino + relativo. Vai no `path`. */
  path: string;
  relativePath: string;
  /** Nome do arquivo em NFC — o que vai como `filename` do multipart. */
  filename: string;
}

export interface UploadPlan {
  uploads: PlannedUpload[];
  /** Pastas vazias a criar (caminho completo), só as folhas. */
  emptyFolders: string[];
  skipped: SkippedItem[];
  systemCount: number;
  totalBytes: number;
}

/** `.DS_Store`, `._arte.pdf`, `Thumbs.db`… — nem é listado ao usuário. */
export function isSystemFileName(name: string): boolean {
  return name.startsWith(".") || SYSTEM_FILE_NAMES.has(name.toLowerCase());
}

/** `__MACOSX`, `.git`, `.Trashes`… — a pasta inteira é ignorada, sem ser lida. */
export function isSystemDirName(name: string): boolean {
  return name.startsWith(".") || SYSTEM_DIR_NAMES.has(name.toLowerCase());
}

function isSystemPath(relativePath: string): boolean {
  const segments = relativePath.split("/");
  const name = segments.pop() ?? "";
  return isSystemFileName(name) || segments.some(isSystemDirName);
}

export function isSupportedUploadFile(file: File, name: string = file.name): boolean {
  const lowerName = name.toLowerCase();
  const hasSupportedExt = SUPPORTED_UPLOAD_EXTENSIONS.some((ext) => lowerName.endsWith(ext));
  return hasSupportedExt && (!file.type || SUPPORTED_UPLOAD_MIME_TYPES.has(file.type));
}

/**
 * O macOS pode entregar nome acentuado decomposto (NFD): «Promoção» digitado
 * e «Promoção» vindo do disco virariam duas pastas iguais na tela.
 */
function cleanRelativePath(raw: string): string {
  return raw
    .normalize("NFC")
    .split("/")
    .filter((segment) => segment !== "" && segment !== ".")
    .join("/");
}

function joinPath(folder: string, relativePath: string): string {
  const base = folder.trim().replace(/^\/+|\/+$/g, "");
  return base ? `${base}/${relativePath}` : relativePath;
}

/** Tamanho como o Postgres conta (`VARCHAR(n)` é em caracteres): pontos de código. */
function charCount(value: string): number {
  return Array.from(value).length;
}

function limitProblem(path: string): string | null {
  if (charCount(path) > MAX_PATH_CHARS) {
    return `Caminho com mais de ${MAX_PATH_CHARS} caracteres.`;
  }
  if (path.split("/").some((segment) => charCount(segment) > MAX_SEGMENT_CHARS)) {
    return `Nome com mais de ${MAX_SEGMENT_CHARS} caracteres.`;
  }
  return null;
}

/** As pastas acima de um caminho: «a/b/x.pdf» (ou a pasta «a/b/c») → «a», «a/b». */
function ancestorsOf(relativePath: string): string[] {
  const segments = relativePath.split("/");
  const out: string[] = [];
  for (let index = 1; index < segments.length; index += 1) {
    out.push(segments.slice(0, index).join("/"));
  }
  return out;
}

/** O que veio do seletor (arquivos soltos ou `webkitdirectory`). */
export function collectFromFileList(files: Iterable<File>): CollectedUpload {
  const candidates: UploadCandidate[] = [];
  let systemCount = 0;
  for (const file of files) {
    const relativePath = cleanRelativePath(file.webkitRelativePath || file.name);
    if (!relativePath) continue;
    if (isSystemPath(relativePath)) {
      systemCount += 1;
      continue;
    }
    candidates.push({ file, relativePath });
  }
  return { candidates, directories: [], systemCount, unreadable: [] };
}

// ---------------------------------------------------------------------------
// Arrastar: a API de entradas do navegador, no mínimo que este código usa.
// Estrutural de propósito — os testes passam árvores falsas.
// ---------------------------------------------------------------------------

export interface EntryLike {
  readonly isFile: boolean;
  readonly isDirectory: boolean;
  readonly name: string;
}

export interface FileEntryLike extends EntryLike {
  file(success: (file: File) => void, error?: (err: unknown) => void): void;
}

export interface DirectoryEntryLike extends EntryLike {
  createReader(): {
    readEntries(success: (entries: EntryLike[]) => void, error?: (err: unknown) => void): void;
  };
}

interface ItemLike {
  readonly kind: string;
  webkitGetAsEntry?: () => EntryLike | null;
}

interface DataTransferLike {
  readonly items?: ArrayLike<ItemLike> | null;
  readonly files: ArrayLike<File> & Iterable<File>;
}

function errorText(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (err && typeof err === "object" && "name" in err) return String((err as { name: unknown }).name);
  return String(err);
}

function readFile(entry: FileEntryLike): Promise<File> {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
}

/** Todas as entradas de uma pasta: `readEntries` devolve em lotes até vir vazio. */
async function readAllEntries(entry: DirectoryEntryLike): Promise<EntryLike[]> {
  const reader = entry.createReader();
  const all: EntryLike[] = [];
  for (;;) {
    const batch = await new Promise<EntryLike[]>((resolve, reject) =>
      reader.readEntries(resolve, reject),
    );
    if (batch.length === 0) return all;
    all.push(...batch);
  }
}

async function walkEntry(entry: EntryLike, parent: string, out: CollectedUpload): Promise<void> {
  const relativePath = cleanRelativePath(parent ? `${parent}/${entry.name}` : entry.name);
  if (!relativePath) return;
  if (entry.isDirectory) {
    if (isSystemDirName(entry.name)) {
      out.systemCount += 1;
      return;
    }
    out.directories.push(relativePath);
    let children: EntryLike[];
    try {
      children = await readAllEntries(entry as DirectoryEntryLike);
    } catch (err) {
      out.unreadable.push({ relativePath, reason: "unreadable", detail: errorText(err) });
      return;
    }
    // Arquivos da pasta em paralelo (`file()` é só metadado); subpastas em
    // sequência, para a ordem da fila seguir a árvore.
    const files = children.filter((child) => child.isFile);
    const dirs = children.filter((child) => child.isDirectory);
    await Promise.all(files.map((child) => walkEntry(child, relativePath, out)));
    for (const dir of dirs) await walkEntry(dir, relativePath, out);
    return;
  }
  if (!entry.isFile) return;
  if (isSystemFileName(entry.name)) {
    out.systemCount += 1;
    return;
  }
  try {
    const file = await readFile(entry as FileEntryLike);
    out.candidates.push({ file, relativePath });
  } catch (err) {
    out.unreadable.push({ relativePath, reason: "unreadable", detail: errorText(err) });
  }
}

/**
 * O que foi solto: arquivos e pastas, com a árvore inteira.
 *
 * **Chamar de dentro do handler do `drop`, sem `await` antes**: as entradas
 * são tiradas do `DataTransfer` na parte síncrona desta função. Navegador sem
 * `webkitGetAsEntry` cai em `dataTransfer.files` (só arquivos soltos, como
 * era antes).
 */
export function collectFromDataTransfer(dataTransfer: DataTransferLike): Promise<CollectedUpload> {
  const items = Array.from(dataTransfer.items ?? []).filter((item) => item.kind === "file");
  const entries = items.map((item) => item.webkitGetAsEntry?.() ?? null);
  if (entries.length === 0 || entries.some((entry) => entry === null)) {
    return Promise.resolve(collectFromFileList(Array.from(dataTransfer.files)));
  }
  return (async () => {
    const out: CollectedUpload = { candidates: [], directories: [], systemCount: 0, unreadable: [] };
    for (const entry of entries as EntryLike[]) await walkEntry(entry, "", out);
    return out;
  })();
}

/**
 * Do que foi coletado ao que vai ser enviado na pasta `targetFolder`: caminho
 * completo de cada arquivo, o que é pulado (e por quê) e as pastas vazias.
 *
 * Pasta vazia é a que ficou sem nenhum arquivo enviado embaixo dela — inclusive
 * a que só tinha `.DS_Store` ou um `.txt`: ela existe na árvore do usuário.
 * Só as folhas são criadas; as de cima aparecem pelo caminho delas.
 */
export function planUpload(collected: CollectedUpload, targetFolder: string): UploadPlan {
  const uploads: PlannedUpload[] = [];
  const skipped: SkippedItem[] = [...collected.unreadable];
  const seen = new Set<string>();
  for (const { file, relativePath } of collected.candidates) {
    const filename = relativePath.slice(relativePath.lastIndexOf("/") + 1);
    if (!isSupportedUploadFile(file, filename)) {
      skipped.push({ relativePath, reason: "unsupported" });
      continue;
    }
    const path = joinPath(targetFolder, relativePath);
    const problem = limitProblem(path);
    if (problem) {
      skipped.push({ relativePath, reason: "too_long", detail: problem });
      continue;
    }
    // Arrastar a mesma coisa duas vezes no mesmo gesto não sobe duas vezes.
    if (seen.has(path)) continue;
    seen.add(path);
    uploads.push({ file, path, relativePath, filename });
  }

  const filled = new Set(uploads.flatMap((upload) => ancestorsOf(upload.relativePath)));
  const empty = Array.from(new Set(collected.directories)).filter((dir) => !filled.has(dir));
  // Pasta vazia com subpasta vazia: a de baixo já traz a de cima.
  const hasEmptyChild = new Set(empty.flatMap(ancestorsOf));
  const emptyFolders: string[] = [];
  for (const dir of empty) {
    if (hasEmptyChild.has(dir)) continue;
    const path = joinPath(targetFolder, dir);
    const problem = limitProblem(path);
    if (problem) {
      skipped.push({ relativePath: `${dir}/`, reason: "too_long", detail: problem });
      continue;
    }
    emptyFolders.push(path);
  }

  return {
    uploads,
    emptyFolders,
    skipped,
    systemCount: collected.systemCount,
    totalBytes: uploads.reduce((sum, upload) => sum + upload.file.size, 0),
  };
}

export function needsConfirmation(plan: UploadPlan): boolean {
  return plan.uploads.length > CONFIRM_ABOVE_FILES || plan.totalBytes > CONFIRM_ABOVE_BYTES;
}

export function skipReasonLabel(reason: SkipReason): string {
  if (reason === "unsupported") return "tipo não aceito";
  if (reason === "too_long") return "caminho longo demais";
  if (reason === "folder_error") return "pasta não criada";
  return "não deu para ler";
}

/** A pasta (relativa) de um item da fila, para mostrar ao lado do nome. */
export function relativeFolderOf(relativePath: string): string {
  const index = relativePath.lastIndexOf("/");
  return index === -1 ? "" : relativePath.slice(0, index);
}

/**
 * A rota da pasta no gerenciador, com cada segmento codificado. Montar
 * `/f/${path}` cru quebrava pasta com `#` (o resto virava hash), `?` (virava
 * query) e `%` — nomes comuns em pasta vinda do disco do cliente. O React
 * Router decodifica o splat de volta em `params["*"]`.
 */
export function folderRoute(path: string): string {
  const clean = path.replace(/^\/+|\/+$/g, "");
  if (!clean) return "/manager";
  return `/f/${clean.split("/").map(encodeURIComponent).join("/")}`;
}
